import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { TextField, AreaField, SelectField } from "@/components/form";
import { api, apiError, fileUrl } from "@/lib/api";

const AREAS = { local: "Local", online: "Online", nationwide: "Nationwide" };
const SOCIALS = ["facebook", "instagram", "linkedin"];

export function BusinessEditDialog({ business, categories, open, onOpenChange, onSaved }) {
  const [f, setF] = useState(business);
  const logoRef = useRef();
  const photoRef = useRef();
  const [uploading, setUploading] = useState(false);
  useEffect(() => { setF(business); }, [business]);
  if (!f) return null;

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleArea = (a) => set("service_areas", (f.service_areas || []).includes(a) ? f.service_areas.filter((x) => x !== a) : [...(f.service_areas || []), a]);

  const doUpload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data.url;
    } catch (e) { toast.error(apiError(e)); return null; }
    finally { setUploading(false); }
  };

  const onLogo = async (e) => { const file = e.target.files?.[0]; if (!file) return; const url = await doUpload(file); if (url) set("logo_url", url); };
  const onPhoto = async (e) => { const file = e.target.files?.[0]; if (!file) return; const url = await doUpload(file); if (url) set("photos", [...(f.photos || []), url]); };

  const save = async () => {
    try {
      const payload = {
        name: f.name, description: f.description, category: f.category, city: f.city, state: f.state,
        service_areas: f.service_areas || [], website: f.website || "", public_phone: f.public_phone || "",
        public_email: f.public_email || "", logo_url: f.logo_url || "", photos: f.photos || [],
        social_links: f.social_links || {}, faith_statement: f.faith_statement || "",
        community_involvement: f.community_involvement || "", is_demo: !!f.is_demo,
      };
      const { data } = await api.patch(`/admin/businesses/${f.id}`, payload);
      toast.success("Listing saved.");
      onSaved(data);
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-business-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl text-navy">Edit listing</DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <TextField label="Business name" name="name" value={f.name || ""} onChange={(e) => set("name", e.target.value)} />
          <AreaField label="Description" name="description" value={f.description || ""} onChange={(e) => set("description", e.target.value)} />
          <SelectField label="Category" name="category" value={f.category} onValueChange={(v) => set("category", v)} placeholder="Category" options={categories} />
          <div className="grid grid-cols-2 gap-4">
            <TextField label="City" name="city" value={f.city || ""} onChange={(e) => set("city", e.target.value)} />
            <TextField label="State" name="state" value={f.state || ""} onChange={(e) => set("state", e.target.value)} />
          </div>
          <div>
            <Label className="text-sm text-navy">Service area</Label>
            <div className="mt-2 flex gap-4">
              {Object.keys(AREAS).map((a) => (
                <label key={a} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={(f.service_areas || []).includes(a)} onCheckedChange={() => toggleArea(a)} className="data-[state=checked]:bg-emerald data-[state=checked]:border-emerald" />
                  {AREAS[a]}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <TextField label="Website" name="website" value={f.website || ""} onChange={(e) => set("website", e.target.value)} />
            <TextField label="Public phone" name="public_phone" value={f.public_phone || ""} onChange={(e) => set("public_phone", e.target.value)} />
          </div>
          <TextField label="Public email" name="public_email" value={f.public_email || ""} onChange={(e) => set("public_email", e.target.value)} />
          <div className="grid grid-cols-3 gap-3">
            {SOCIALS.map((s) => (
              <TextField key={s} label={s[0].toUpperCase() + s.slice(1)} name={s} value={f.social_links?.[s] || ""}
                onChange={(e) => set("social_links", { ...(f.social_links || {}), [s]: e.target.value })} placeholder="https://" />
            ))}
          </div>
          <AreaField label="Faith statement (owner-submitted)" name="faith_statement" value={f.faith_statement || ""} onChange={(e) => set("faith_statement", e.target.value)} />
          <AreaField label="Community involvement (owner-submitted)" name="community_involvement" value={f.community_involvement || ""} onChange={(e) => set("community_involvement", e.target.value)} />

          {/* Logo */}
          <div>
            <Label className="text-sm text-navy">Logo</Label>
            <div className="mt-2 flex items-center gap-4">
              <div className="h-16 w-16 rounded-lg bg-ivory-alt overflow-hidden flex items-center justify-center">
                {f.logo_url ? <img src={fileUrl(f.logo_url)} alt="" className="h-full w-full object-cover" /> : <span className="text-xs text-muted-foreground">None</span>}
              </div>
              <input ref={logoRef} type="file" accept="image/*" hidden onChange={onLogo} />
              <Button type="button" variant="outline" className="rounded-full" disabled={uploading} onClick={() => logoRef.current?.click()}><Upload className="mr-2 h-4 w-4" /> Upload logo</Button>
              {f.logo_url && <Button type="button" variant="ghost" className="text-ruby" onClick={() => set("logo_url", "")}>Remove</Button>}
            </div>
          </div>

          {/* Photos */}
          <div>
            <Label className="text-sm text-navy">Photos</Label>
            <div className="mt-2 flex flex-wrap gap-3">
              {(f.photos || []).map((p, i) => (
                <div key={i} className="relative h-16 w-16 rounded-lg overflow-hidden">
                  <img src={fileUrl(p)} alt="" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => set("photos", f.photos.filter((_, idx) => idx !== i))} className="absolute top-0 right-0 bg-ruby text-white p-0.5"><X className="h-3 w-3" /></button>
                </div>
              ))}
              <input ref={photoRef} type="file" accept="image/*" hidden onChange={onPhoto} />
              <button type="button" onClick={() => photoRef.current?.click()} disabled={uploading} className="h-16 w-16 rounded-lg border border-dashed border-border flex items-center justify-center text-muted-foreground hover:border-emerald"><Upload className="h-5 w-5" /></button>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={!!f.is_demo} onCheckedChange={(v) => set("is_demo", v)} className="data-[state=checked]:bg-navy data-[state=checked]:border-navy" />
            Mark as demo (hidden from the public directory)
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button className="rounded-full bg-emerald hover:bg-emerald-dark text-white" onClick={save} disabled={uploading} data-testid="save-business-btn">Save changes</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
